import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { useMemo } from 'react';

import type { AddressAutocompleteProps, PlaceType } from './types';

import { useAddressAutocomplete } from './use-address-autocomplete';

export const AddressAutocomplete = (props: AddressAutocompleteProps) => {
  const hooks = useAddressAutocomplete(props);

  const valueObject: PlaceType | null = useMemo(() => {
    return hooks.inputValue ? { description: hooks.inputValue, place_id: '' } : null;
  }, [hooks.inputValue]);

  const minLength = props.minLength ?? 4;
  const shouldOpen = hooks.open && hooks.inputValue.length >= minLength;
  const muiSize = props.size === 'small' ? 'small' : 'medium';

  return (
    <Autocomplete<PlaceType, false, false, false>
      id={props.id ?? 'address-autocomplete'}
      fullWidth={props.fullWidth ?? true}
      disabled={props.disabled}
      size={muiSize}
      options={hooks.options}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      isOptionEqualToValue={(option, val) =>
        option.place_id === val.place_id || option.description === val.description
      }
      filterOptions={(x) => x}
      autoComplete
      includeInputInList
      value={valueObject}
      open={shouldOpen}
      onOpen={() => hooks.setOpen(true)}
      onClose={() => hooks.setOpen(false)}
      loading={hooks.loading}
      loadingText="Buscando direcciones..."
      noOptionsText={
        hooks.loading
          ? 'Buscando direcciones...'
          : hooks.inputValue.length < minLength
            ? `Escribe al menos ${minLength} caracteres...`
            : 'No se encontraron direcciones'
      }
      onChange={(_, newValue) => {
        hooks.onSelectOption(newValue);
      }}
      inputValue={hooks.inputValue}
      onInputChange={(_, newInputValue, reason) => {
        hooks.onInputChange(newInputValue, reason);
      }}
      className={props.className}
      sx={props.sx}
      renderInput={(params) => (
        <TextField
          {...params}
          name={props.name}
          label={props.label}
          placeholder={props.placeholder ?? 'Dirección'}
          variant={props.variant ?? 'outlined'}
          size={muiSize}
          required={props.required}
          error={props.error}
          helperText={props.helperText}
          fullWidth
          onBlur={() => {
            props.onBlur?.(hooks.inputValue);
          }}
          slotProps={{
            input: {
              ...params.InputProps,
              startAdornment: props.startAdornment ? (
                <>
                  {props.startAdornment}
                  {params.InputProps.startAdornment}
                </>
              ) : (
                params.InputProps.startAdornment
              ),
              endAdornment: (
                <>
                  {hooks.loading ? (
                    <CircularProgress color="inherit" size={muiSize === 'small' ? 18 : 22} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
            htmlInput: {
              ...params.inputProps,
              autoComplete: 'off',
              role: 'presentation',
              'data-form-type': 'other',
              'data-lpignore': 'true',
              'data-1p-ignore': 'true',
            },
          }}
        />
      )}
    />
  );
};

export default AddressAutocomplete;
